"use client";

import AccordionSection from "@/components/longhouse/AccordionSection";
import RewardSettingsForm, {
    DEFAULT_ACADEMY_REWARD,
    DEFAULT_STREAM_TEAM_REWARD,
    RewardSettingsValues,
} from "@/components/longhouse/RewardSettingsForm";
import { STREAM_SETTINGS_MUTATION } from "@/graphql/mutations/streamMutations";
import { STREAM_SETTINGS_QUERY } from "@/graphql/queries/streamQueries";
import { useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiSave, FiSettings } from "react-icons/fi";

interface GeneralSettings {
    goldenWoh: boolean;
    doubleSentry: boolean;
    acceptingApplications: boolean;
    doubleShieldMateRewards: boolean;
}

const DEFAULT_GENERAL: GeneralSettings = {
    goldenWoh: false,
    doubleSentry: false,
    acceptingApplications: true,
    doubleShieldMateRewards: false,
};

interface RaidRpSettings {
    raidRpPerViewer: number;
    raidRpMinimumViewers: number;
    academyRaidRpEnabled: boolean;
}

const DEFAULT_RAID_RP: RaidRpSettings = {
    raidRpPerViewer: 1,
    raidRpMinimumViewers: 10,
    academyRaidRpEnabled: false,
};

interface SubscriberRpSettings {
    subscriberRpAmountTier1000: number;
    subscriberRpAmountTier2000: number;
    subscriberRpAmountTier3000: number;
    academySubscriberRpEnabled: boolean;
}

const DEFAULT_SUBSCRIBER_RP: SubscriberRpSettings = {
    subscriberRpAmountTier1000: 25,
    subscriberRpAmountTier2000: 25,
    subscriberRpAmountTier3000: 25,
    academySubscriberRpEnabled: false,
};

interface FreeRunestoneSettings {
    freeRunestoneRpThreshold: number;
}

const DEFAULT_FREE_RUNESTONE: FreeRunestoneSettings = {
    freeRunestoneRpThreshold: 10000,
};

type RewardPrefix = "streamTeamReward" | "academyReward";
type StreamSettingsData = Record<string, unknown>;

function readReward(
    settings: StreamSettingsData | undefined,
    prefix: RewardPrefix,
    fallback: RewardSettingsValues,
): RewardSettingsValues {
    const read = <T,>(suffix: string, defaultValue: T): T => {
        const value = settings?.[`${prefix}${suffix}`];
        return value === null || value === undefined
            ? defaultValue
            : (value as T);
    };

    return {
        name: read("Name", fallback.name) || fallback.name,
        description:
            read("Description", fallback.description) || fallback.description,
        cost: read("Cost", fallback.cost),
        rpAmount: read("RpAmount", fallback.rpAmount),
        backgroundColor:
            read("BackgroundColor", fallback.backgroundColor) ||
            fallback.backgroundColor,
        userInputRequired: read(
            "UserInputRequired",
            fallback.userInputRequired,
        ),
        skipQueue: read("SkipQueue", fallback.skipQueue),
        globalCooldownEnabled: read(
            "GlobalCooldownEnabled",
            fallback.globalCooldownEnabled,
        ),
        maxPerStreamEnabled: read(
            "MaxPerStreamEnabled",
            fallback.maxPerStreamEnabled,
        ),
        maxPerUserPerStreamEnabled: read(
            "MaxPerUserPerStreamEnabled",
            fallback.maxPerUserPerStreamEnabled,
        ),
        globalCooldownSeconds: read(
            "GlobalCooldownSeconds",
            fallback.globalCooldownSeconds,
        ),
        limitPerStream: read("LimitPerStream", fallback.limitPerStream),
        limitPerUserPerStream: read(
            "LimitPerUserPerStream",
            fallback.limitPerUserPerStream,
        ),
    };
}

function rewardVariables(
    prefix: RewardPrefix,
    values: RewardSettingsValues,
): Record<string, unknown> {
    return {
        [`${prefix}Name`]: values.name,
        [`${prefix}Description`]: values.description,
        [`${prefix}Cost`]: values.cost,
        [`${prefix}RpAmount`]: values.rpAmount,
        [`${prefix}BackgroundColor`]: values.backgroundColor,
        [`${prefix}UserInputRequired`]: values.userInputRequired,
        [`${prefix}SkipQueue`]: values.skipQueue,
        [`${prefix}GlobalCooldownEnabled`]: values.globalCooldownEnabled,
        [`${prefix}MaxPerStreamEnabled`]: values.maxPerStreamEnabled,
        [`${prefix}MaxPerUserPerStreamEnabled`]:
            values.maxPerUserPerStreamEnabled,
        [`${prefix}GlobalCooldownSeconds`]: values.globalCooldownSeconds,
        [`${prefix}LimitPerStream`]: values.limitPerStream,
        [`${prefix}LimitPerUserPerStream`]: values.limitPerUserPerStream,
    };
}

const GENERAL_TOGGLES: {
    key: keyof GeneralSettings;
    label: string;
    hint: string;
    onLabel: string;
    offLabel: string;
}[] = [
    {
        key: "goldenWoh",
        label: "Golden WoH",
        hint: "Enable or disable Golden WoH feature for the stream",
        onLabel: "Enabled",
        offLabel: "Disabled",
    },
    {
        key: "acceptingApplications",
        label: "Stream Team Applications",
        hint: "Allow players to apply to join the stream team",
        onLabel: "Open",
        offLabel: "Closed",
    },
    {
        key: "doubleSentry",
        label: "Double Sentry",
        hint: "Double the watch time and VP earned per sentry",
        onLabel: "Enabled",
        offLabel: "Disabled",
    },
    {
        key: "doubleShieldMateRewards",
        label: "Double Shield Mate Rewards",
        hint: "Double the rewards given for shield mate referrals",
        onLabel: "Enabled",
        offLabel: "Disabled",
    },
];

export default function StreamSettingsPage() {
    const [general, setGeneral] = useState<GeneralSettings>(DEFAULT_GENERAL);
    const [streamTeamReward, setStreamTeamReward] =
        useState<RewardSettingsValues>(DEFAULT_STREAM_TEAM_REWARD);
    const [academyReward, setAcademyReward] = useState<RewardSettingsValues>(
        DEFAULT_ACADEMY_REWARD,
    );
    const [raidRp, setRaidRp] = useState<RaidRpSettings>(DEFAULT_RAID_RP);
    const [subscriberRp, setSubscriberRp] = useState<SubscriberRpSettings>(
        DEFAULT_SUBSCRIBER_RP,
    );
    const [freeRunestone, setFreeRunestone] = useState<FreeRunestoneSettings>(
        DEFAULT_FREE_RUNESTONE,
    );
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const { data, loading, error, refetch } = useQuery(STREAM_SETTINGS_QUERY);
    const settings = data?.streamSettings as StreamSettingsData | undefined;

    const [updateStreamSettings] = useMutation(STREAM_SETTINGS_MUTATION, {
        onCompleted: () => {
            setSaving(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            refetch();
        },
        onError: (mutationError) => {
            console.error("Error updating stream settings:", mutationError);
            setSaving(false);
        },
    });

    const persisted = useMemo(
        () => ({
            general: {
                goldenWoh: Boolean(settings?.goldenWoh),
                doubleSentry: Boolean(settings?.doubleSentry),
                acceptingApplications:
                    (settings?.acceptingApplications as boolean | undefined) ??
                    true,
                doubleShieldMateRewards: Boolean(
                    settings?.doubleShieldMateRewards,
                ),
            },
            streamTeamReward: readReward(
                settings,
                "streamTeamReward",
                DEFAULT_STREAM_TEAM_REWARD,
            ),
            academyReward: readReward(
                settings,
                "academyReward",
                DEFAULT_ACADEMY_REWARD,
            ),
            raidRp: {
                raidRpPerViewer:
                    (settings?.raidRpPerViewer as number | undefined) ??
                    DEFAULT_RAID_RP.raidRpPerViewer,
                raidRpMinimumViewers:
                    (settings?.raidRpMinimumViewers as number | undefined) ??
                    DEFAULT_RAID_RP.raidRpMinimumViewers,
                academyRaidRpEnabled: Boolean(settings?.academyRaidRpEnabled),
            },
            subscriberRp: {
                subscriberRpAmountTier1000:
                    (settings?.subscriberRpAmountTier1000 as
                        | number
                        | undefined) ??
                    DEFAULT_SUBSCRIBER_RP.subscriberRpAmountTier1000,
                subscriberRpAmountTier2000:
                    (settings?.subscriberRpAmountTier2000 as
                        | number
                        | undefined) ??
                    DEFAULT_SUBSCRIBER_RP.subscriberRpAmountTier2000,
                subscriberRpAmountTier3000:
                    (settings?.subscriberRpAmountTier3000 as
                        | number
                        | undefined) ??
                    DEFAULT_SUBSCRIBER_RP.subscriberRpAmountTier3000,
                academySubscriberRpEnabled: Boolean(
                    settings?.academySubscriberRpEnabled,
                ),
            },
            freeRunestone: {
                freeRunestoneRpThreshold:
                    (settings?.freeRunestoneRpThreshold as
                        | number
                        | undefined) ??
                    DEFAULT_FREE_RUNESTONE.freeRunestoneRpThreshold,
            },
        }),
        [settings],
    );

    const applyPersisted = useCallback(() => {
        setGeneral(persisted.general);
        setStreamTeamReward(persisted.streamTeamReward);
        setAcademyReward(persisted.academyReward);
        setRaidRp(persisted.raidRp);
        setSubscriberRp(persisted.subscriberRp);
        setFreeRunestone(persisted.freeRunestone);
    }, [persisted]);

    useEffect(() => {
        if (settings) {
            applyPersisted();
        }
    }, [settings, applyPersisted]);

    const hasChanges =
        Boolean(settings) &&
        JSON.stringify({
            general,
            streamTeamReward,
            academyReward,
            raidRp,
            subscriberRp,
            freeRunestone,
        }) !== JSON.stringify(persisted);

    const handleSave = async () => {
        setSaving(true);
        setSaveSuccess(false);

        try {
            await updateStreamSettings({
                variables: {
                    ...general,
                    ...raidRp,
                    ...subscriberRp,
                    ...freeRunestone,
                    ...rewardVariables("streamTeamReward", streamTeamReward),
                    ...rewardVariables("academyReward", academyReward),
                },
            });
        } catch (err) {
            console.error("Save failed:", err);
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="flex items-center justify-center p-8">
                        <div className="text-slate-600 dark:text-slate-400">
                            Loading stream settings...
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-300 bg-red-50 p-6 shadow-lg dark:border-red-700 dark:bg-red-900/20">
                    <div className="text-red-800 dark:text-red-400">
                        Error loading stream settings: {error.message}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* LEFT: Settings Form */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <div className="mb-4 flex items-center gap-2">
                        <FiSettings className="h-6 w-6 text-slate-700 dark:text-slate-300" />
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                            Stream Settings
                        </h1>
                    </div>

                    <div className="space-y-3">
                        <AccordionSection
                            title="General"
                            description="Stream-wide feature toggles"
                            defaultOpen
                        >
                            {GENERAL_TOGGLES.map((toggle) => {
                                const value = general[toggle.key];
                                return (
                                    <div key={toggle.key} className="space-y-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            {toggle.label}
                                        </label>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={value}
                                                aria-label={toggle.label}
                                                onClick={() =>
                                                    setGeneral({
                                                        ...general,
                                                        [toggle.key]: !value,
                                                    })
                                                }
                                                className={clsx(
                                                    "focus:ring-brand relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-slate-900",
                                                    value
                                                        ? "bg-brand"
                                                        : "bg-slate-300 dark:bg-slate-600",
                                                )}
                                            >
                                                <span
                                                    className={clsx(
                                                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                                        value
                                                            ? "translate-x-6"
                                                            : "translate-x-1",
                                                    )}
                                                />
                                            </button>
                                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                                {value
                                                    ? toggle.onLabel
                                                    : toggle.offLabel}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {toggle.hint}
                                        </p>
                                    </div>
                                );
                            })}
                        </AccordionSection>

                        <AccordionSection
                            title="RAVENGUARD Channel Point Reward"
                            description="Used when full stream team members sync their reward"
                        >
                            <RewardSettingsForm
                                values={streamTeamReward}
                                onChange={(patch) =>
                                    setStreamTeamReward({
                                        ...streamTeamReward,
                                        ...patch,
                                    })
                                }
                                namePlaceholder={
                                    DEFAULT_STREAM_TEAM_REWARD.name
                                }
                                descriptionPlaceholder={
                                    DEFAULT_STREAM_TEAM_REWARD.description
                                }
                            />
                        </AccordionSection>

                        <AccordionSection
                            title="VRX Academy Channel Point Reward"
                            description="Used when academy members sync their reward"
                        >
                            <RewardSettingsForm
                                values={academyReward}
                                onChange={(patch) =>
                                    setAcademyReward({
                                        ...academyReward,
                                        ...patch,
                                    })
                                }
                                namePlaceholder={
                                    DEFAULT_ACADEMY_REWARD.name
                                }
                                descriptionPlaceholder={
                                    DEFAULT_ACADEMY_REWARD.description
                                }
                            />
                        </AccordionSection>

                        <AccordionSection
                            title="Raid RP"
                            description="RP awarded when a member raids another member"
                        >
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    RP Per Raid Viewer
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    value={raidRp.raidRpPerViewer}
                                    onChange={(e) =>
                                        setRaidRp({
                                            ...raidRp,
                                            raidRpPerViewer: Math.max(
                                                1,
                                                Number.parseInt(
                                                    e.target.value || "1",
                                                    10,
                                                ),
                                            ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Minimum Qualifying Raid Viewers
                                </label>
                                <input
                                    type="number"
                                    min={1}
                                    value={raidRp.raidRpMinimumViewers}
                                    onChange={(e) =>
                                        setRaidRp({
                                            ...raidRp,
                                            raidRpMinimumViewers: Math.max(
                                                1,
                                                Number.parseInt(
                                                    e.target.value || "1",
                                                    10,
                                                ),
                                            ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Raids below this size are recorded but earn
                                    no RP.
                                </p>
                            </div>

                            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 dark:border-slate-700">
                                <div>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Include Academy Members
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Allow raids to or from Academy members
                                        to earn RP.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={raidRp.academyRaidRpEnabled}
                                    aria-label="Include Academy Members"
                                    onClick={() =>
                                        setRaidRp({
                                            ...raidRp,
                                            academyRaidRpEnabled:
                                                !raidRp.academyRaidRpEnabled,
                                        })
                                    }
                                    className={clsx(
                                        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                                        raidRp.academyRaidRpEnabled
                                            ? "bg-brand"
                                            : "bg-slate-300 dark:bg-slate-600",
                                    )}
                                >
                                    <span
                                        className={clsx(
                                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                            raidRp.academyRaidRpEnabled
                                                ? "translate-x-6"
                                                : "translate-x-1",
                                        )}
                                    />
                                </button>
                            </div>
                        </AccordionSection>

                        <AccordionSection
                            title="Subscriber RP"
                            description="RP awarded when a player completes 30 days of subscription"
                        >
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Tier 1 ($4.99) RP Amount
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={
                                        subscriberRp.subscriberRpAmountTier1000
                                    }
                                    onChange={(e) =>
                                        setSubscriberRp({
                                            ...subscriberRp,
                                            subscriberRpAmountTier1000:
                                                Math.max(
                                                    0,
                                                    Number.parseInt(
                                                        e.target.value || "0",
                                                        10,
                                                    ),
                                                ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Tier 2 ($9.99) RP Amount
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={
                                        subscriberRp.subscriberRpAmountTier2000
                                    }
                                    onChange={(e) =>
                                        setSubscriberRp({
                                            ...subscriberRp,
                                            subscriberRpAmountTier2000:
                                                Math.max(
                                                    0,
                                                    Number.parseInt(
                                                        e.target.value || "0",
                                                        10,
                                                    ),
                                                ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Tier 3 ($24.99) RP Amount
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={
                                        subscriberRp.subscriberRpAmountTier3000
                                    }
                                    onChange={(e) =>
                                        setSubscriberRp({
                                            ...subscriberRp,
                                            subscriberRpAmountTier3000:
                                                Math.max(
                                                    0,
                                                    Number.parseInt(
                                                        e.target.value || "0",
                                                        10,
                                                    ),
                                                ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                            </div>

                            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 dark:border-slate-700">
                                <div>
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Include Academy Members
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Allow Academy members to earn subscriber
                                        RP.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={
                                        subscriberRp.academySubscriberRpEnabled
                                    }
                                    aria-label="Include Academy Members"
                                    onClick={() =>
                                        setSubscriberRp({
                                            ...subscriberRp,
                                            academySubscriberRpEnabled:
                                                !subscriberRp.academySubscriberRpEnabled,
                                        })
                                    }
                                    className={clsx(
                                        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                                        subscriberRp.academySubscriberRpEnabled
                                            ? "bg-brand"
                                            : "bg-slate-300 dark:bg-slate-600",
                                    )}
                                >
                                    <span
                                        className={clsx(
                                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                            subscriberRp.academySubscriberRpEnabled
                                                ? "translate-x-6"
                                                : "translate-x-1",
                                        )}
                                    />
                                </button>
                            </div>
                        </AccordionSection>

                        <AccordionSection
                            title="Free Runestone Claims"
                            description="RP required to earn one free Runestone claim"
                        >
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    RP Required per Claim
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={1000}
                                    value={
                                        freeRunestone.freeRunestoneRpThreshold
                                    }
                                    onChange={(e) =>
                                        setFreeRunestone({
                                            ...freeRunestone,
                                            freeRunestoneRpThreshold: Math.max(
                                                0,
                                                Number.parseInt(
                                                    e.target.value || "0",
                                                    10,
                                                ),
                                            ),
                                        })
                                    }
                                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                                />
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Set to 0 to disable. Players earn 1 free
                                    claim per this many RP across all team
                                    members.
                                </p>
                            </div>
                        </AccordionSection>

                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={handleSave}
                                disabled={!hasChanges || saving}
                                className={clsx(
                                    "flex items-center gap-2 rounded-md px-4 py-2 font-medium transition-colors",
                                    hasChanges && !saving
                                        ? "bg-brand hover:bg-brand/90 cursor-pointer text-white"
                                        : "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
                                )}
                            >
                                <FiSave className="h-4 w-4" />
                                {saving ? "Saving..." : "Save Changes"}
                            </button>

                            {hasChanges && (
                                <button
                                    onClick={applyPersisted}
                                    disabled={saving}
                                    className="rounded-md border border-slate-300 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                                >
                                    Reset
                                </button>
                            )}
                        </div>

                        {saveSuccess && (
                            <div className="rounded-lg bg-green-100 p-3 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                <p className="text-sm font-medium">
                                    Settings saved successfully!
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT: Documentation Panel */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Stream Settings Guide
                    </h2>

                    <div className="space-y-4 text-sm leading-relaxed text-slate-700 dark:text-slate-400">
                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Golden WoH
                            </h3>
                            <p>
                                Controls the Golden Wheel of Hamingja feature
                                during streaming. When enabled, the player will
                                earn two tiers of the Tag of Hamingja when they
                                redeem LUCKY! in the portal.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Stream Team Applications
                            </h3>
                            <p>
                                Controls whether new players can submit
                                applications to join the stream team. When
                                disabled, users will see a message that
                                applications are currently closed. Invitations
                                from administrators will continue to work
                                regardless of this setting.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Double Sentry
                            </h3>
                            <p>
                                When enabled, each sentry will earn the player 2
                                Hours watch time instead of 1 as well as 100VP
                                instead of 50VP.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Double Shield Mate Rewards
                            </h3>
                            <p>
                                Controls the rewards given when a player submits
                                their shield with a shield mate (referral).
                                Normal rewards: 1 Wheel of Hamingja token + 5000
                                VP to both players. Double rewards: 2 tokens +
                                10000 VP to both players.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                RAVENGUARD Channel Point Reward
                            </h3>
                            <p>
                                The reward template pushed to full stream team
                                member channels when they create or sync their
                                channel point reward. Each of these members
                                carves their own personal runestone.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                VRX Academy Channel Point Reward
                            </h3>
                            <p>
                                A separate reward template used by Academy
                                members, who all share the standard VRX Academy
                                runestone. When an Academy member is promoted,
                                this reward is removed from their channel so
                                they can carve their own runestone and enable
                                the RAVENGUARD reward.
                            </p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-slate-900 dark:text-slate-100">
                                Raid RP
                            </h3>
                            <p>
                                When one stream team member raids another, the
                                raider earns RP on the raided member's channel.
                                Raids smaller than the minimum are logged but
                                earn nothing, which stops small repeat raids
                                being farmed. Requires the Raid RP feature flag
                                to be on.
                            </p>
                        </div>

                        <div className="bg-brand/50 text-brand dark:bg-brand/50 dark:text-brand rounded-lg p-3">
                            {" "}
                            <p className="text-xs">
                                <strong>Note:</strong> Changes to these settings
                                take effect immediately and will apply on the
                                next relevant event during the stream. Rewards
                                already live on a member's channel only pick up
                                changes when synced.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
